/**
 * Copyright (c) 2019, Trend Micro, Inc.
 * All rights reserved.
 */
import { delay } from '../util';
const API_VERSION = 'v1';
const DEFAULT_REGION = 'us-1';
const devUrl = DEPLOYMENT_TARGET === 'alpha' || DEPLOYMENT_TARGET === 'dev' ? `/trend-us-1` : '';

export const isSessionValid = async (retries = 3, backoffPeriod = 3000) => {
  const url = `${devUrl}/api/sessions/current`;
  try {
    let rID = '';
    if (typeof window !== 'undefined') {
      rID = window.sessionStorage.getItem('rID');
    }

    if (!rID) {
      console.error('rID is missing');
      return;
    }

    let response = await fetch(url, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'api-version': API_VERSION,
        updateLastActivity: false,
        rID,
      },
    });

    if (response.ok) {
      // if HTTP-status is 200-299
      // get the response body (the method explained below)
      const json = await readApiJson(response);
      // console.log('TCL: isSessionValid -> json', json);
      // console.log('session is valid: ', json.RID === rID);
      return json.RID === rID;
    } else if (response.status === 502) {
      if (retries === 0) throw new Error(response);
      if (retries > 0) {
        await delay(backoffPeriod); //3 seconds
        return await isSessionValid(retries - 1);
      }
    } else {
      return false;
    }
  } catch (error) {
    console.error(`API request to "${url}" failed`, error);
    return false;
  }
};

const readApiJson = async (response) => {
  const contentType = response.headers.get('content-type');
  if (!contentType || contentType.indexOf('application/json') === -1) {
    throw new Error(`Invalid response content-type: ${contentType}`);
  }

  let contentLength = response.headers.get('Content-Length');
  try {
    contentLength = parseInt(contentLength);
  } catch (e) {
    // best effort, header may not have been sent by server
    console.error('Unable to read content-length');
  }

  let json;
  try {
    if (contentLength === 0) {
      json = await Promise.resolve({});
    } else {
      json = await response.json();
    }
  } catch (e) {
    throw new Error(`Api returned invalid json body: \n\t${e}`);
  }
  if (json.error) {
    throw new Error(`Api returned error: ${json.error}`);
  } else {
    return json;
  }
};

export const getAccountDetails = async () => {
  const url = `${devUrl}/api/accountdetails/current`;
  try {
    let rID = '';
    if (typeof window !== 'undefined') {
      rID = window.sessionStorage.getItem('rID');
    }
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'api-version': API_VERSION,
        rID,
      },
    });

    if (response.ok) {
      // if HTTP-status is 200-299
      // get the response body
      const json = await readApiJson(response);
      return json;
    } else {
      return null;
    }
  } catch (error) {
    console.error(`API request to "${url}" failed`, error);
  }
};

export const getUserDetails = async () => {
  const url = `${devUrl}/api/administrators/current`;
  try {
    let rID = '';
    if (typeof window !== 'undefined') {
      rID = window.sessionStorage.getItem('rID');
    }
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'api-version': API_VERSION,
        updateLastActivity: false,
        rID,
      },
    });

    if (response.ok) {
      // if HTTP-status is 200-299
      // get the response body
      const json = await readApiJson(response);
      return json;
    } else {
      return null;
    }
  } catch (error) {
    console.error(`API request to "${url}" failed`, error);
  }
};

export const getRegionPrefixedEndPoint = (url, region) => {
  if (!url || !region) {
    throw 'URL or region parameters missing.';
  }

  let regionPrefix;

  if (region === DEFAULT_REGION) {
    regionPrefix = '';
  } else {
    regionPrefix = region;
  }

  if (regionPrefix) {
    return '/' + regionPrefix + url;
  }

  return url;
};

export const doDeleteCurrentSession = async () => {
  const region = window.sessionStorage.getItem('REGION_PREFIX') || DEFAULT_REGION;
  const url = getRegionPrefixedEndPoint('/api/sessions/current', region);
  try {
    let rID;
    if (typeof window !== 'undefined') {
      rID = window.sessionStorage.getItem('rID') || undefined;
    }

    if (!rID) {
      console.warn('rID is missing');
      return false;
    }

    const response = await fetch(url, {
      method: 'DELETE',
      cache: 'no-store',
      // Use keepalive to prevent the browser from cancelling the request if the user navigates away from the page
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'api-version': API_VERSION,
        rID: rID,
      },
    });

    return response.ok;
  } catch (error) {
    console.error(`API request to "${url}" failed`, error);
    return false;
  }
};
